import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  FaMousePointer,
  FaClock,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaUser,
  FaCalendarAlt,
} from 'react-icons/fa';
import { fetchEngagementAnalytics } from '../services/engagementService';

// ─── Constants ─────────────────────────────────────────────────────────────────

/** Auto-refresh interval in milliseconds */
const REFRESH_INTERVAL_MS = 60_000;

/**
 * Default/fallback metrics shown before data loads.
 */
const DEFAULT_METRICS = {
  totalSessions: 0,
  bounceRate: '0%',
  engagementRate: '0%',
  avgSessionDuration: '0s',
  pagesPerSession: 0,
  bounceSessions: 0,
};

/**
 * Thresholds used to color-code bounce rate values.
 * Above HIGH  → red, above MED → yellow, otherwise → green.
 */
const BOUNCE_RATE_THRESHOLDS = { HIGH: 40, MED: 25 };

// ─── Pure Helpers ──────────────────────────────────────────────────────────────

/**
 * Transforms the raw API engagement object into the flat metrics shape
 * consumed by the stat cards.
 *
 * @param {Object} engagement - `data.engagement` from the API response
 * @returns {Object} Formatted metrics
 */
const buildMetricsData = (engagement) => {
  const bounceRate = engagement.bounceRate ?? 0;
  return {
    totalSessions: engagement.totalSessions ?? 0,
    bounceRate: `${Math.round(bounceRate)}%`,
    engagementRate: `${(100 - bounceRate).toFixed(2)}%`,
    avgSessionDuration: engagement.avgSessionDuration
      ? `${Math.round(engagement.avgSessionDuration)}s`
      : '0s',
    pagesPerSession: engagement.pagesPerSession ?? 0,
    bounceSessions: engagement.bounceSessions ?? 0,
  };
};

/**
 * Computes per-page metrics from the raw records array for a given page.
 *
 * @param {Array}  records  - Full records array from `data.records`
 * @param {string} page     - Selected page path
 * @returns {Object} Computed stats for that page
 */
const computePageMetrics = (records, page) => {
  const pageRecords = records.filter((r) => r.page === page);
  const total = pageRecords.length;
  if (total === 0) return { totalSessions: 0, bounces: 0, bounceRate: '0%', engagementRate: '0%', avgDuration: '0s' };

  const bounces = pageRecords.filter((r) => r.isBouncePage).length;
  const engaged = pageRecords.filter((r) => r.hasUserInteracted).length;
  const totalDuration = pageRecords.reduce((sum, r) => sum + r.sessionDurationSeconds, 0);

  return {
    totalSessions: total,
    bounces,
    bounceRate: `${Math.round((bounces / total) * 100)}%`,
    engagementRate: `${((engaged / total) * 100).toFixed(2)}%`,
    avgDuration: `${(totalDuration / total).toFixed(2)}s`,
  };
};

/**
 * Returns a Tailwind text-color class based on the numeric bounce rate value.
 *
 * @param {string} rateStr - e.g. "42%"
 * @returns {string} Tailwind class
 */
const bounceRateColor = (rateStr) => {
  const val = parseFloat(rateStr);
  if (val > BOUNCE_RATE_THRESHOLDS.HIGH) return 'text-red-400';
  if (val > BOUNCE_RATE_THRESHOLDS.MED) return 'text-yellow-400';
  return 'text-green-400';
};

// ─── Sub-components ────────────────────────────────────────────────────────────

/**
 * A single summary metric card.
 *
 * @param {string}   title  - Card label
 * @param {*}        value  - Numeric or string value to display
 * @param {React.FC} icon   - Icon component
 * @param {string}   color  - Hex accent color for icon background tint
 */
const StatCard = ({ title, value, icon: Icon, color = '#4ade80' }) => (
  <div className="bg-[#1D1E22] rounded-lg p-4 flex flex-col border border-gray-800 hover:border-gray-600 transition">
    <span className="text-gray-400 text-sm mb-2">{title}</span>
    <div className="flex justify-between items-center">
      <span className="text-white text-3xl md:text-4xl font-bold">{value}</span>
      <div
        className="p-2 rounded-full min-w-[40px] min-h-[40px] flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
    </div>
  </div>
);

/**
 * Inline loading banner shown while data is fetching.
 */
const LoadingBanner = () => (
  <div className="mb-4 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg flex items-center gap-3">
    <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
    <span className="text-blue-300">Loading engagement analytics...</span>
  </div>
);

/**
 * Error banner with a reload retry button.
 *
 * @param {string} message - Error text to display
 */
const ErrorBanner = ({ message }) => (
  <div className="mb-4 p-4 bg-red-900/20 border border-red-700/50 rounded-lg flex items-start gap-3">
    <FaExclamationCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
    <div className="flex-1">
      <p className="text-red-300 font-semibold">Error Loading Analytics</p>
      <p className="text-red-200 text-sm mt-1">{message}</p>
    </div>
    <button
      onClick={() => window.location.reload()}
      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap flex-shrink-0"
    >
      Retry
    </button>
  </div>
);

/**
 * Sidebar page-selector list with an "All Pages" option.
 *
 * @param {string[]}    pages        - Unique page paths to list
 * @param {string|null} selectedPage - Currently active page, or null for all
 * @param {Function}    onSelect     - Callback with the selected page or null
 */
const PageSelector = ({ pages, selectedPage, onSelect }) => (
  <div className="bg-[#1D1E22] rounded-lg border border-[#2D2F33] p-4 mb-4">
    <h3 className="font-medium text-white mb-4">Pages</h3>
    <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
      {pages.length > 0 ? (
        <>
          <button
            onClick={() => onSelect(null)}
            className={`px-3 py-2 rounded text-sm text-left transition ${
              selectedPage === null ? 'bg-blue-600 text-white' : 'bg-[#2D2F33] text-gray-100 hover:bg-[#3D3F43]'
            }`}
          >
            All Pages
          </button>
          {pages.map((page) => (
            <button
              key={page}
              onClick={() => onSelect(page)}
              title={page}
              className={`px-3 py-2 rounded text-sm text-left break-words transition ${
                selectedPage === page ? 'bg-blue-600 text-white' : 'bg-[#2D2F33] text-gray-100 hover:bg-[#3D3F43]'
              }`}
            >
              {page}
            </button>
          ))}
        </>
      ) : (
        <span className="text-gray-400 text-sm">No pages tracked yet</span>
      )}
    </div>
  </div>
);

/**
 * Displays computed metrics for the currently selected page.
 *
 * @param {Object} metrics - Output of `computePageMetrics`
 */
const PageStatsPanel = ({ metrics }) => {
  const rows = [
    { label: 'Sessions',    value: metrics.totalSessions,   cls: 'text-white' },
    { label: 'Bounces',     value: metrics.bounces,         cls: 'text-white' },
    { label: 'Bounce Rate', value: metrics.bounceRate,      cls: bounceRateColor(metrics.bounceRate) },
    { label: 'Engagement',  value: metrics.engagementRate,  cls: 'text-green-400' },
    { label: 'Avg Duration',value: metrics.avgDuration,     cls: 'text-blue-400' },
  ];

  return (
    <div className="bg-[#1D1E22] rounded-lg border border-[#2D2F33] p-4">
      <h3 className="font-medium text-white mb-4">Page Statistics</h3>
      <div className="space-y-3 text-sm">
        {rows.map(({ label, value, cls }) => (
          <div key={label} className="flex justify-between">
            <span className="text-gray-400">{label}:</span>
            <span className={`font-medium ${cls}`}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

/**
 * EngagementAnalyticsDashboard
 *
 * Fetches and displays bounce rate, engagement metrics, and page-level
 * session analysis. Refreshes automatically every 60 seconds.
 *
 * @param {string} projectTitle - Label shown in the header badge
 */
const EngagementAnalyticsDashboard = ({ projectTitle = 'All Experiences' }) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [engagementData, setEngagementData] = useState(null);
  const [metricsData, setMetricsData]       = useState(DEFAULT_METRICS);
  const [selectedPage, setSelectedPage]     = useState(null);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);

  // ── Data Fetching ──────────────────────────────────────────────────────────

  /** Loads engagement data from the API and updates derived metric state. */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEngagementAnalytics();
      console.log('Engagement Analytics:', data);
      setEngagementData(data);

      if (data?.engagement) {
        setMetricsData(buildMetricsData(data.engagement));
      }
    } catch (err) {
      console.error('Engagement fetch error:', err);
      setError(err.message ?? 'Failed to fetch engagement data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and set up polling
  useEffect(() => {
    loadData();
    const intervalId = setInterval(loadData, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [loadData]);

  // ── Derived Data ───────────────────────────────────────────────────────────

  /**
   * Deduplicated list of page paths from the API response.
   * Supports both `pagePerformance` and `data.records` shapes.
   */
  const uniquePages = useMemo(() => {
    if (engagementData?.pagePerformance?.length) {
      return engagementData.pagePerformance.map((p) => p.page);
    }
    if (engagementData?.data?.records?.length) {
      return [...new Set(engagementData.data.records.map((r) => r.page))];
    }
    return [];
  }, [engagementData]);

  /**
   * Per-page stats for the sidebar panel, computed only when a page is selected.
   */
  const selectedPageMetrics = useMemo(() => {
    if (!selectedPage || !engagementData?.data?.records) return null;
    return computePageMetrics(engagementData.data.records, selectedPage);
  }, [engagementData, selectedPage]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col bg-[#18191C] min-h-screen overflow-auto p-4 md:p-6 w-full">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl md:text-[28px] font-bold text-white">
          Engagement & Bounce Rate Analytics
        </h1>
        <div className="bg-[#2D2F33] rounded-lg px-4 py-2 flex items-center gap-2 w-full sm:w-auto shrink-0">
          <span className="text-gray-300 text-sm whitespace-nowrap">Project:</span>
          <span className="font-medium text-white text-sm truncate max-w-[200px]">{projectTitle}</span>
        </div>
      </div>

      {/* ── Date Range Indicator ───────────────────────────────────────────── */}
      <div className="mb-4 px-4 py-2 bg-[#2D2F33] rounded-lg flex items-center gap-2 w-full sm:w-auto">
        <FaCalendarAlt className="w-4 h-4 text-white" />
        <span className="text-white text-sm">Last 7 days</span>
      </div>

      {/* ── Status Banners ─────────────────────────────────────────────────── */}
      {error   && <ErrorBanner message={error} />}
      {loading && <LoadingBanner />}

      {/* ── Summary Stat Cards ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard title="Total Sessions" value={metricsData.totalSessions}      icon={FaUser}               color="#3b82f6" />
        <StatCard title="Bounce Rate"    value={metricsData.bounceRate}          icon={FaExclamationTriangle} color="#ef4444" />
        <StatCard title="Avg Duration"   value={metricsData.avgSessionDuration}  icon={FaClock}               color="#f59e0b" />
        <StatCard title="Pages/Session"  value={metricsData.pagesPerSession}     icon={FaMousePointer}        color="#ec4899" />
        <StatCard title="Bounce Sessions" value={metricsData.bounceSessions}     icon={FaExclamationCircle}   color="#f87171" />
      </div>

      {/* ── Main Content ───────────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-6">

        {/* Page Performance Table */}
        <div className="flex-1 bg-[#1D1E22] rounded-lg border border-[#2D2F33] p-4 md:p-6">
          <h2 className="text-xl font-bold text-white mb-4">Page Performance</h2>

          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading analytics data...</div>
          ) : engagementData?.pagePerformance?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-400 text-sm font-medium">
                    {['Page', 'Sessions', 'Bounces', 'Bounce Rate', 'Engagement', 'Avg Duration'].map((h) => (
                      <th key={h} className={`py-3 px-4 ${h === 'Page' ? 'text-left' : 'text-center'}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {engagementData.pagePerformance.map((page) => (
                    <tr
                      key={page.page}
                      onClick={() => setSelectedPage(page.page)}
                      className="border-b border-gray-800 hover:bg-[#2D2F33] transition cursor-pointer"
                    >
                      <td className="py-3 px-4 text-white text-sm">{page.page}</td>
                      <td className="py-3 px-4 text-center text-gray-300 text-sm">{page.totalSessions}</td>
                      <td className="py-3 px-4 text-center text-gray-300 text-sm">{page.bounces}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-sm font-medium ${bounceRateColor(page.bounceRate)}`}>
                          {page.bounceRate}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-green-400 text-sm">{page.engagementRate}</td>
                      <td className="py-3 px-4 text-center text-blue-400 text-sm">{page.avgSessionDuration}s</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">No page analytics available</div>
          )}
        </div>

        {/* Sidebar: page selector + selected-page stats */}
        <div className="w-full lg:w-80">
          <PageSelector
            pages={uniquePages}
            selectedPage={selectedPage}
            onSelect={setSelectedPage}
          />
          {selectedPageMetrics && <PageStatsPanel metrics={selectedPageMetrics} />}
        </div>
      </div>
    </div>
  );
};

export default EngagementAnalyticsDashboard;